// Models
import { SanitizedMember } from 'models/Member'
import moment from 'moment'
import {
  PaymentGateway,
  TransactionResponse,
  TransactionItemTypeAPIv2,
  TransactionRequestBody,
} from 'models/Transaction'

// Redux services
import {
  createRecurringDonationPlan,
  createRecurringDonationPlanTransaction,
} from 'redux/services/recurringDonationPlan'
import transactionsApi from 'redux/services/transaction'

// Local
import { RootState, TypedDispatch } from 'redux/store'
import getApiv2PaymentType from './getApiv2PaymentType'
import getPaymentSource from '../shared/getPaymentSource'
import { FinalizeOrderArgs } from '../types'

const createDraftTransaction = async (
  store: RootState,
  dispatch: TypedDispatch<RootState>,
  member: SanitizedMember,
  args: FinalizeOrderArgs,
): Promise<TransactionResponse> => {
  const { cid, frequency, currency, baseAmount } = args
  const { firstName, lastName, phone } = store.order.billingDetails

  const paymentDetails = store.order.paymentDetails

  const answers = Object.values(store.order.answers).map((answer) => {
    return { question_id: answer.questionId, answer: answer.answer }
  })

  const transactionPayload: TransactionRequestBody = {
    billing_address1: store.order.billingDetails.address.line1,
    billing_address2: store.order.billingDetails.address.line2,
    billing_city: store.order.billingDetails.address.city,
    billing_country: store.order.billingDetails.address.country,
    billing_first_name: firstName,
    billing_last_name: lastName,
    billing_postal_code: store.order.billingDetails.address.postalCode,
    billing_state: store.order.billingDetails.address.state,
    frequency: args.frequency,
    company_name: store.order.billingDetails.companyName,
    designation_id: args.designationId,
    hide_amount: store.order.paymentDetails.isOrderPrivate,
    is_anonymous: store.order.paymentDetails.isOrderAnonymous,
    items: [
      {
        quantity: 1,
        type: TransactionItemTypeAPIv2.DONATION,
        product_name: 'Donation',
        /** Donation amount */
        price: baseAmount,
        /** Final amount (with or without fees depending on dcf) */
        raw_final_price: store.order.paymentDetails.amount,
      },
    ],
    is_reprocess: false,
    is_gift_aid: false,
    member_id: member.id,
    member_country: store.order.billingDetails.address.country,
    member_email_address: store.order.billingDetails.email,
    // CL-49071 first name & last name must be explicitly provided separately for APIv2 to function as expected
    member_first_name: firstName,
    member_last_name: lastName,
    // not clear if this is actually used when FN+LN are provided
    member_name: `${firstName} ${lastName}`,
    member_phone: phone,
    raw_currency_code: currency,
    payment_type: getApiv2PaymentType(paymentDetails.type),
    payment_method: 'Classy Pay', // All transactions in checkout are processed by classypay api
    payment_gateway: PaymentGateway[paymentDetails.processor] ?? PaymentGateway.CLASSYPAY,
    payment_source: getPaymentSource(paymentDetails.type, store),
    status: 'incomplete',
    answers,
    ...store.order.grossAdjustment,
    comment: store.order.donationOptions.comment,
    employer_match: {
      employer_id: String(store.order.donationOptions.employerMatchCompanyId),
      employer_name: store.order.donationOptions.employerMatchCompanyName,
      employer_search_text: store.order.donationOptions.employerMatchCompanyName,
    },
  }

  /**
   * Create an RDP or one time donation based whether a frequency is set.
   */
  if (frequency && frequency !== 'one-time') {
    const rdp = await dispatch(
      createRecurringDonationPlan.initiate({
        /** Required Fields */
        member_id: member.id,
        // TODO: Does raw_donation_amount include fees w/dcf
        raw_donation_amount: baseAmount,
        raw_currency_code: currency,
        donation_amount: store.order.paymentDetails.amount,
        started_at: moment().format(),
        recur_until: args.recurringEndDate,
        status: 'draft',
        recurring_donatable_id: cid,
        recurring_donatable_type: 'campaign',

        /** Additional Fields */
        designation_id: args.designationId,
        first_name: firstName,
        last_name: lastName,
        address: store.order.billingDetails.address.line1,
        address2: store.order.billingDetails.address.line2,
        city: store.order.billingDetails.address.city,
        state: store.order.billingDetails.address.state,
        country: store.order.billingDetails.address.country,
        postal_code: store.order.billingDetails.address.postalCode,
        company_name: store.order.billingDetails.companyName,
        frequency: frequency,
        is_anonymous: store.order.paymentDetails.isOrderAnonymous,
        hide_amount: store.order.paymentDetails.isOrderPrivate,
        ...store.order.grossAdjustment,
        // CL-49071 manually send supporter info to APIv2
        // if the supporter already exists, this will be ignored
        supporter: {
          first_name: firstName,
          last_name: lastName,
          phone,
        },
      }),
    ).unwrap()

    return await dispatch(
      createRecurringDonationPlanTransaction.initiate({
        recurringDonationPlanId: rdp.id,
        transaction: transactionPayload,
      }),
    ).unwrap()
  } else {
    return await dispatch(
      transactionsApi.endpoints.createTransaction.initiate({
        campaignId: cid,
        transaction: transactionPayload,
      }),
    ).unwrap()
  }
}

export default createDraftTransaction
